/**
 * Developer mode settings for testing the game.
 *
 * Two things must both be true before a helper appears, and this module is where
 * they meet. The account must be on the developer list, which is checked in the
 * database and resolved once at start-up into the flag below; and the toggle in
 * GachID must be on. Each helper reads `isDevMode()` (or a named flag) rather than
 * inventing its own condition, so turning the toggle off - or signing in with an
 * account that is not on the list - reliably removes all of them, and a release
 * build cannot leak a testing affordance into normal play.
 *
 * The toggle lives in this browser rather than in the account, so it does not
 * follow a developer between devices: syncing it would mean adding a column to
 * `player_profiles` and a database migration, which these helpers do not warrant.
 * The list membership, which is the part that has to be enforced rather than
 * remembered, is in the database. See
 * `docs/decisions/DEC-008-developer-access-whitelist.md`.
 */

const STORAGE_KEY = 'garaimon.dev.v1';

/**
 * Whether the signed-in account is on the developer list. Set once at start-up,
 * because `isDevMode()` is called from rendering and has to stay synchronous.
 *
 * Defaults to false, and a failed check deliberately leaves it false: a build that
 * cannot confirm who is signed in has no developers rather than all of them.
 */
let developerAccount = false;

/** Records the result of the start-up developer-list check. */
export function setDeveloperAccount(value: boolean): void {
  developerAccount = value;
}

/** Whether the account signed in on this device is a developer. */
export function hasDeveloperAccount(): boolean {
  return developerAccount;
}

export interface DevSettings {
  /**
   * Master switch. Every development helper is gated by this, so a new helper
   * only has to check `isDevMode()` to be switched off with everything else.
   */
  enabled: boolean;
}

/**
 * A checkbox in GachID, driven by this list rather than by hand-written
 * markup. Adding a future development helper means adding the field to
 * `DevSettings` above, its default below, and one entry here; the GachID
 * screen itself does not change.
 */
export interface DevFlagDefinition {
  key: keyof DevSettings;
  label: string;
  hint: string;
}

export const DEV_FLAGS: DevFlagDefinition[] = [
  {
    key: 'enabled',
    label: 'Developer mode',
    hint: 'Adds testing helpers across the game. Currently: choose any opponent and any partner Garaimon, without adding them to your roster.',
  },
];

const defaults = (): DevSettings => ({ enabled: false });

/**
 * Reads the saved settings. A missing or unreadable record falls back to the
 * defaults and says so, rather than failing quietly; a corrupted record that
 * silently enabled developer mode would be worse than the error itself.
 */
export function loadDevSettings(): DevSettings {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return defaults();
  try {
    const parsed = JSON.parse(stored) as Partial<DevSettings>;
    return { ...defaults(), ...parsed };
  } catch (error) {
    console.warn(`Garaimon: the developer settings record could not be read, so developer mode is off. Remove localStorage key "${STORAGE_KEY}" to clear it.`, error);
    return defaults();
  }
}

export function saveDevSettings(settings: DevSettings): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

/** Whether the testing helpers are active. Requires both the account and the toggle. */
export function isDevMode(): boolean {
  return developerAccount && loadDevSettings().enabled;
}

/** Flips one flag and persists the result, returning the new settings. */
export function setDevFlag(key: keyof DevSettings, value: boolean): DevSettings {
  const next = { ...loadDevSettings(), [key]: value };
  saveDevSettings(next);
  return next;
}
